import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, User, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from "@/hooks/useAuth";

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login, isAdmin, logout } = useAuth();

    // Redirect if already logged in as admin
    useEffect(() => {
        if (isAdmin) {
            navigate('/admin/dashboard');
        }
    }, [isAdmin, navigate]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            console.log("--- ADMIN LOGIN UI: INITIATING AUTH ---");
            const authenticatedUser = await login(email.trim(), password.trim());
            console.log("--- ADMIN LOGIN UI: AUTH SUCCESSFUL ---", { role: authenticatedUser.role });

            if (authenticatedUser.role === 'admin') {
                toast.success('Login successful. Welcome back!');
                navigate('/admin/dashboard', { replace: true });
            } else {
                toast.error('Access denied. You are not an admin.');
                // MANDATORY: Immediately sign out the student from admin terminal
                await logout();
            }
        } catch (error: any) {
            console.error("--- ADMIN LOGIN UI: AUTH FAILED ---", error);
            const message = error.message || 'Login failed. Please check your details.';

            if (message.toLowerCase().includes("email not confirmed")) {
                toast.error("Please confirm your email or contact system admin.", {
                    description: "Verification link sent to your inbox."
                });
            } else {
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#000814] flex items-center justify-center p-6 overflow-hidden relative">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />

            <div className="w-full max-w-md relative z-10">
                <div className="text-center mb-10">
                    <div className="w-20 h-20 rounded-[2.5rem] bg-accent/10 flex items-center justify-center mx-auto mb-6 border border-accent/20 shadow-premium group hover:rotate-6 transition-transform duration-500">
                        <Shield className="w-10 h-10 text-accent group-hover:scale-110 transition-transform" />
                    </div>
                    <h1 className="text-3xl font-black text-white uppercase tracking-tighter mb-2">Admin <span className="text-accent italic">Login</span></h1>
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Please login to manage your website</p>
                </div>

                <div className="bg-white/[0.03] backdrop-blur-3xl border border-white/10 rounded-[2.5rem] p-10 shadow-2xl">
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Email Address</label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-accent transition-colors" />
                                <Input
                                    type="email"
                                    placeholder="admin@ratchagan.com"
                                    className="h-14 bg-white/[0.02] border-white/5 pl-12 focus:border-accent/40 focus:ring-accent/10 transition-all rounded-2xl text-white font-medium"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Password</label>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-accent transition-colors" />
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    className="h-14 bg-white/[0.02] border-white/5 pl-12 focus:border-accent/40 focus:ring-accent/10 transition-all rounded-2xl text-white font-medium"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full h-16 btn-gold rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-premium hover:shadow-gold-strong transition-all duration-500"
                        >
                            {isLoading ? 'Logging in...' : 'Login'}
                            {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                    </form>
                </div>

                <p className="text-center mt-8 text-[10px] font-black uppercase tracking-widest text-slate-600">
                    Admin Only Area
                </p>
            </div>
        </div>
    );
};

export default AdminLogin;
